import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// CTCAC broadband amenity requirements (Section 10325, per the Dec 10, 2025
// approved regulations). Worth 2 points (3 for Rural projects).
const REQUIREMENTS = [
    { key: 'free', label: 'Free to tenants', detail: 'High-speed internet provided free of charge to every low-income unit.' },
    { key: 'speed', label: '≥ 25 Mbps download', detail: 'Minimum average download speed of 25 megabits/second.' },
    { key: 'term', label: '15-year commitment', detail: 'Service maintained for a minimum of 15 years.' },
    { key: 'timing', label: 'Live ≤ 6 months after PIS', detail: 'Available within 6 months of the project’s placed-in-service date.' },
];

const TcacBroadbandEstimatorPage = () => {
    usePageSeo({
        title: 'TCAC Broadband Requirements & Points Estimator',
        description: 'Free TCAC broadband requirements estimator. Check whether your project earns the CTCAC broadband amenity — 25 Mbps, free to all units, 15-year term.',
        keywords: 'TCAC broadband points, CTCAC broadband requirement, LIHTC internet scoring, 9% tax credit amenities, affordable housing broadband, TCAC QAP scoring, BUILDLAB',
    });

    const [met, setMet] = useState({ free: false, speed: false, term: false, timing: false });
    const [rural, setRural] = useState(false);

    const toggle = (k) => setMet((m) => ({ ...m, [k]: !m[k] }));

    const result = useMemo(() => {
        const missing = REQUIREMENTS.filter((r) => !met[r.key]);
        const maxPts = rural ? 3 : 2;
        const eligible = missing.length === 0;
        return { eligible, points: eligible ? maxPts : 0, maxPts, missing };
    }, [met, rural]);

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'TCAC Broadband Requirements Estimator'} pagesub={'Estimator'} pageTop={'Affordable Housing'} />
                <JsonLd data={webAppSchema({ name: 'TCAC Broadband Requirements & Points Estimator', description: 'Free TCAC broadband requirements estimator — check whether your project earns the CTCAC broadband amenity points.', path: '/tcac-broadband-estimator' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Will your project earn the CTCAC broadband points?</h2>
                                <p>
                                    Under CTCAC 9% scoring, providing high-speed internet to residents is a competitive amenity
                                    worth <strong>2 points (3 points for Rural projects)</strong>. To qualify, the service must meet
                                    all four requirements below. Check the ones your plan already covers to see where you stand.
                                </p>
                                <p className="mb-0">
                                    Broadband is one line of the Site Amenities score. To estimate every category, use the{' '}
                                    <Link to="/tcac-scoring-estimator">TCAC 9% Scoring Estimator</Link>.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Requirement checklist */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px' }}>
                                    <div className="input_title mb-2">Does your broadband plan include…</div>
                                    {REQUIREMENTS.map((r) => (
                                        <button type="button" key={r.key} onClick={() => toggle(r.key)}
                                            style={{
                                                width: '100%', textAlign: 'left', display: 'flex', gap: 14, alignItems: 'flex-start',
                                                padding: '14px 16px', marginBottom: 10, borderRadius: 10, cursor: 'pointer',
                                                border: `1px solid ${met[r.key] ? 'var(--bs-primary)' : 'var(--bs-border-color)'}`,
                                                background: met[r.key] ? 'var(--bs-primary-bg-subtle)' : '#fff',
                                            }}>
                                            <span style={{
                                                flex: 'none', width: 24, height: 24, borderRadius: 6, marginTop: 1,
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                background: met[r.key] ? 'var(--bs-primary)' : '#fff',
                                                border: `1px solid ${met[r.key] ? 'var(--bs-primary)' : 'var(--bs-border-color)'}`, color: '#fff',
                                            }}>
                                                {met[r.key] && <i className="fa-solid fa-check" aria-hidden="true" style={{ fontSize: '.75rem' }}></i>}
                                            </span>
                                            <span>
                                                <span style={{ fontWeight: 600, color: 'var(--bs-dark)' }}>{r.label}</span>
                                                <span style={{ display: 'block', fontSize: '.84rem', color: 'var(--bs-secondary-color)' }}>{r.detail}</span>
                                            </span>
                                        </button>
                                    ))}
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, cursor: 'pointer', fontSize: '.9rem' }}>
                                        <input type="checkbox" checked={rural} onChange={() => setRural((v) => !v)} />
                                        This is a <strong>Rural</strong> project (broadband amenity is worth 3 points)
                                    </label>
                                </div>
                            </div>

                            {/* Verdict */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', height: '100%' }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Estimated broadband points</div>
                                    <div style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1.1 }}>
                                        {result.points}<span style={{ fontSize: '1.1rem', fontWeight: 500, opacity: 0.8 }}> / {result.maxPts}</span>
                                    </div>
                                    <div style={{ fontWeight: 700, color: result.eligible ? '#7CFFB2' : '#FFC9A3', marginBottom: 10 }}>
                                        {result.eligible ? 'Qualifies for the broadband amenity' : `${result.missing.length} requirement${result.missing.length === 1 ? '' : 's'} to go`}
                                    </div>
                                    {result.eligible ? (
                                        <p style={{ fontSize: '.88rem', opacity: 0.9 }}>Your plan meets all four CTCAC broadband requirements — capture the {result.maxPts} points and document internet availability in your application.</p>
                                    ) : (
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                            {result.missing.map((r) => (
                                                <li key={r.key} style={{ fontSize: '.85rem', opacity: 0.92, padding: '4px 0' }}>
                                                    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" style={{ color: '#FFC9A3', marginRight: 8 }}></i>{r.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-4" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Design my broadband scope">Design my broadband scope</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="TCAC Broadband Estimator"
                                        summary={`${result.points} / ${result.maxPts} broadband points — ${result.eligible ? 'qualifies' : result.missing.length + ' requirement(s) remaining'}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 820 }}>
                            Source: CTCAC Regulations (Dec 10, 2025 approved version), Section 10325 amenities. This is a planning
                            estimator — always confirm point values and requirements against the current QAP for your funding round.
                            BUILDLAB designs the in-unit and common-area broadband infrastructure that earns these points.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default TcacBroadbandEstimatorPage;
